/**
 * Onboarding seed copy — single source of truth.
 *
 * These strings previously lived duplicated in the web and mobile i18n locale
 * dictionaries (`onboarding.project.*` / `onboarding.tasks.*`). Seeding moved
 * server-side, so the API needs them too; this module is the one place they are
 * defined. The brand-name placeholder that the i18n dictionaries carry in
 * `onboarding.tasks.welcome` is already interpolated here with each locale's
 * `brand.name`, so consumers can use these strings verbatim.
 *
 * Task order matches `apps/web/src/lib/mock-data.ts` `buildOnboardingData`:
 * welcome, quickAdd, organize, schedule, priority, drag, gantt, reports, finishMe.
 */

export const ONBOARDING_LOCALES = ['en', 'zh-CN', 'zh-TW', 'ja', 'ko', 'de', 'es', 'fr'] as const;
export type OnboardingLocale = (typeof ONBOARDING_LOCALES)[number];

export interface OnboardingCopy {
  projectName: string;
  tasks: { title: string; description: string | null }[];
}

export const ONBOARDING_COPY: Record<OnboardingLocale, OnboardingCopy> = {
  en: {
    projectName: 'Welcome',
    tasks: [
      {
        title: '👋 Welcome to DoneLine!',
        description:
          "Click the checkbox to your left to mark this done. That's how you complete a task.",
      },
      {
        title: '⚡ Try Quick Add: press Q or click the button in the top-left',
        description:
          'Type a task title and press Enter. You can add it to a project with @project, tag with #tag, set priority with p1-p4.',
      },
      {
        title: '📁 Group related tasks into Projects',
        description: "Click '+ New Project' in the sidebar to create one. Drag tasks into it.",
      },
      {
        title: '🗓 Set a date to plan ahead',
        description:
          'Tasks scheduled today appear in the Today view. Try opening this task and giving it a due date.',
      },
      {
        title: '🔥 Use priorities (P1=High, P4=None)',
        description: 'Press 1 / 2 / 3 / 4 in the task drawer to set priority quickly.',
      },
      {
        title: '✋ Drag to reorder',
        description: 'Hover a task and use the grip icon on the left to drag it to a new position.',
      },
      {
        title: '📊 Visualize with Gantt chart',
        description: 'Open any project and switch to "Gantt" tab to see tasks on a timeline.',
      },
      {
        title: '📈 Reflect with Weekly Report',
        description: 'See what you completed and what got carried over each week.',
      },
      { title: "✅ Mark this checklist done — you're ready!", description: null },
    ],
  },
  'zh-CN': {
    projectName: '欢迎',
    tasks: [
      {
        title: '👋 欢迎使用 DoneLine！',
        description: '点击左侧的复选框把这条标记为完成 —— 这就是完成任务的方式。',
      },
      {
        title: '⚡ 试试快速捕获：按 Q 键，或左上角的快速捕获按钮',
        description:
          '输入任务名按 Enter 即可。可以用 @项目名 归到项目、#标签名 打标签、p1-p4 设优先级。',
      },
      {
        title: '📁 用项目把相关任务组织起来',
        description: '点击左侧栏「+ 新建项目」创建。把任务拖进去整理。',
      },
      {
        title: '🗓 设个日期来安排时间',
        description: '设了今天的任务会自动出现在「今日」视图。试试打开这条任务并设个截止日期。',
      },
      {
        title: '🔥 用优先级（P1 高 … P4 无）',
        description: '在任务详情里按 1 / 2 / 3 / 4 快速切换优先级。',
      },
      {
        title: '✋ 拖动重新排序',
        description: '把鼠标移到任务上，左侧会出现拖动手柄，按住可以拖到新位置。',
      },
      {
        title: '📊 用甘特图看时间线',
        description: '打开任意项目，切到「甘特图」标签，看任务在时间轴上的分布。',
      },
      { title: '📈 用周报回顾', description: '每周看你完成了什么、哪些被推迟了。' },
      { title: '✅ 把这个清单完成 —— 你已经准备好了！', description: null },
    ],
  },
  'zh-TW': {
    projectName: '歡迎',
    tasks: [
      {
        title: '👋 歡迎使用 DoneLine！',
        description: '點選左側的核取方塊把這條標記為完成 —— 這就是完成任務的方式。',
      },
      {
        title: '⚡ 試試快速捕捉：按 Q 鍵，或左上角的快速捕捉按鈕',
        description:
          '輸入任務名按 Enter 即可。可以用 @專案名 歸至專案、#標籤名 加標籤、p1-p4 設優先級。',
      },
      {
        title: '📁 用專案把相關任務組織起來',
        description: '點左側欄「+ 新增專案」建立。把任務拖入整理。',
      },
      {
        title: '🗓 設定日期來安排時間',
        description: '設了今天的任務會自動出現在「今日」視圖。試試開啟這條任務並設個截止日期。',
      },
      {
        title: '🔥 使用優先級（P1 高 … P4 無）',
        description: '在任務詳情中按 1 / 2 / 3 / 4 快速切換優先級。',
      },
      {
        title: '✋ 拖動重新排序',
        description: '把游標移至任務上，左側會出現拖動把手，按住可拖至新位置。',
      },
      {
        title: '📊 用甘特圖看時間軸',
        description: '開啟任一專案，切換至「甘特圖」標籤，看任務在時間軸上的分佈。',
      },
      { title: '📈 用週報回顧', description: '每週看你完成了什麼、哪些被推遲了。' },
      { title: '✅ 把這個清單完成 —— 你已準備好了！', description: null },
    ],
  },
  ja: {
    projectName: 'ようこそ',
    tasks: [
      {
        title: '👋 DoneLine へようこそ！',
        description:
          '左側のチェックボックスをクリックして完了にしてみましょう。これがタスク完了の操作です。',
      },
      {
        title: '⚡ クイック追加を試す: Q キーまたは左上のボタン',
        description:
          'タスク名を入力して Enter。@プロジェクト名 でプロジェクト指定、#タグ名 でタグ付け、p1-p4 で優先度設定。',
      },
      {
        title: '📁 関連タスクをプロジェクトにまとめる',
        description: '左側の「+ 新規プロジェクト」をクリック。タスクをドラッグして整理。',
      },
      {
        title: '🗓 日付を設定して計画',
        description:
          '今日のタスクは自動で「今日」ビューに表示されます。このタスクを開いて期限を設定してみましょう。',
      },
      {
        title: '🔥 優先度を活用 (P1 高 … P4 なし)',
        description: 'タスク詳細で 1 / 2 / 3 / 4 を押すと優先度を素早く切り替え。',
      },
      {
        title: '✋ ドラッグで並べ替え',
        description: 'タスクにマウスを乗せると左にハンドルが出ます。掴んで好きな位置へ。',
      },
      {
        title: '📊 ガントチャートで可視化',
        description:
          'プロジェクトを開いて「ガントチャート」タブに切り替えると、時間軸上でタスクを確認できます。',
      },
      {
        title: '📈 週次レポートで振り返り',
        description: '毎週、何を完了したか、何が持ち越されたかを確認しましょう。',
      },
      { title: '✅ このリストを完了 — 準備完了です！', description: null },
    ],
  },
  ko: {
    projectName: '환영',
    tasks: [
      {
        title: '👋 DoneLine에 오신 것을 환영합니다!',
        description: '왼쪽 체크박스를 클릭해 완료로 표시하세요. 이것이 작업을 완료하는 방법입니다.',
      },
      {
        title: '⚡ 빠른 추가 시도: Q 키 또는 좌측 상단 버튼',
        description:
          '작업 제목을 입력하고 Enter. @프로젝트로 분류, #태그로 라벨, p1-p4로 우선순위 지정.',
      },
      {
        title: '📁 관련 작업을 프로젝트로 묶기',
        description: '왼쪽에서 "+ 새 프로젝트" 클릭. 작업을 드래그해 정리.',
      },
      {
        title: '🗓 날짜를 설정해 계획',
        description:
          '오늘 일정 작업은 자동으로 "오늘" 보기에 나타납니다. 이 작업을 열어 마감일을 설정해보세요.',
      },
      {
        title: '🔥 우선순위 활용 (P1 높음 … P4 없음)',
        description: '작업 상세에서 1 / 2 / 3 / 4 를 누르면 우선순위 빠른 전환.',
      },
      {
        title: '✋ 드래그로 순서 변경',
        description: '작업 위에 마우스를 올리면 왼쪽에 핸들이 나타납니다. 잡고 원하는 위치로.',
      },
      {
        title: '📊 간트 차트로 시각화',
        description: '프로젝트를 열어 "간트" 탭으로 전환하면 시간축에서 작업을 볼 수 있습니다.',
      },
      {
        title: '📈 주간 리포트로 회고',
        description: '매주 무엇을 완료했고 무엇이 미뤄졌는지 확인하세요.',
      },
      { title: '✅ 이 체크리스트 완료 — 이제 준비됐어요!', description: null },
    ],
  },
  de: {
    projectName: 'Willkommen',
    tasks: [
      {
        title: '👋 Willkommen bei DoneLine!',
        description:
          'Klicke auf die Checkbox links, um diese Aufgabe als erledigt zu markieren. So schließt man Aufgaben ab.',
      },
      {
        title: '⚡ Schnellerfassung testen: Q drücken oder den Knopf oben links',
        description:
          'Titel eingeben und Enter. @projekt zum Zuweisen, #tag zum Etikettieren, p1-p4 für Priorität.',
      },
      {
        title: '📁 Verwandte Aufgaben in Projekten gruppieren',
        description: 'Klicke „+ Neues Projekt" in der Seitenleiste. Aufgaben hineinziehen.',
      },
      {
        title: '🗓 Datum setzen, um zu planen',
        description:
          'Heute fällige Aufgaben erscheinen automatisch in der Heute-Ansicht. Öffne diese Aufgabe und gib ihr eine Frist.',
      },
      {
        title: '🔥 Prioritäten nutzen (P1=Hoch, P4=Keine)',
        description: 'Drücke 1 / 2 / 3 / 4 im Detail, um schnell die Priorität zu ändern.',
      },
      {
        title: '✋ Ziehen zum Sortieren',
        description:
          'Maus über eine Aufgabe — links erscheint ein Greifer. Greife und ziehe sie an die neue Position.',
      },
      {
        title: '📊 Mit Gantt visualisieren',
        description:
          'Öffne ein Projekt und wechsle zum Tab „Gantt", um Aufgaben auf einer Zeitachse zu sehen.',
      },
      {
        title: '📈 Mit dem Wochenbericht reflektieren',
        description: 'Sieh jede Woche, was du erledigt und was du verschoben hast.',
      },
      { title: '✅ Diese Liste abhaken — du bist bereit!', description: null },
    ],
  },
  es: {
    projectName: 'Bienvenida',
    tasks: [
      {
        title: '👋 ¡Bienvenido a DoneLine!',
        description:
          'Haz clic en la casilla de la izquierda para marcar esta tarea como hecha. Así se completan las tareas.',
      },
      {
        title: '⚡ Prueba la captura rápida: pulsa Q o el botón superior izquierdo',
        description:
          'Escribe el título y pulsa Enter. Usa @proyecto para asignarlo, #etiqueta para etiquetar, p1-p4 para prioridad.',
      },
      {
        title: '📁 Agrupa tareas relacionadas en Proyectos',
        description: 'Pulsa "+ Nuevo proyecto" en la barra lateral. Arrastra tareas dentro.',
      },
      {
        title: '🗓 Pon una fecha para planificar',
        description:
          'Las tareas con fecha de hoy aparecen en la vista Hoy. Abre esta tarea y dale una fecha límite.',
      },
      {
        title: '🔥 Usa prioridades (P1=Alta, P4=Ninguna)',
        description: 'Pulsa 1 / 2 / 3 / 4 en el detalle para cambiar prioridad rápidamente.',
      },
      {
        title: '✋ Arrastra para reordenar',
        description:
          'Pasa el ratón por una tarea y aparecerá un asa a la izquierda. Arrástrala donde quieras.',
      },
      {
        title: '📊 Visualiza con Gantt',
        description:
          'Abre cualquier proyecto y cambia a la pestaña "Gantt" para ver las tareas en una línea de tiempo.',
      },
      {
        title: '📈 Reflexiona con el Informe semanal',
        description: 'Mira qué completaste y qué se pospuso cada semana.',
      },
      { title: '✅ Marca esta lista como hecha — ¡estás listo!', description: null },
    ],
  },
  fr: {
    projectName: 'Bienvenue',
    tasks: [
      {
        title: '👋 Bienvenue dans DoneLine !',
        description:
          "Cliquez sur la case à gauche pour la marquer comme faite. C'est ainsi qu'on termine une tâche.",
      },
      {
        title: '⚡ Essayez la capture rapide : appuyez sur Q ou le bouton en haut à gauche',
        description:
          'Tapez le titre et appuyez sur Entrée. @projet pour assigner, #étiquette pour étiqueter, p1-p4 pour la priorité.',
      },
      {
        title: '📁 Regroupez les tâches dans des Projets',
        description:
          'Cliquez « + Nouveau projet » dans la barre latérale. Glissez les tâches dedans.',
      },
      {
        title: '🗓 Définissez une date pour planifier',
        description:
          "Les tâches du jour apparaissent automatiquement dans la vue Aujourd'hui. Ouvrez cette tâche et donnez-lui une échéance.",
      },
      {
        title: '🔥 Utilisez les priorités (P1=Haute, P4=Aucune)',
        description: 'Appuyez sur 1 / 2 / 3 / 4 dans le détail pour définir la priorité.',
      },
      {
        title: '✋ Glissez pour réordonner',
        description:
          'Survolez une tâche, une poignée apparaît à gauche. Saisissez-la pour la déplacer.',
      },
      {
        title: '📊 Visualisez avec le Gantt',
        description:
          "Ouvrez un projet et passez à l'onglet « Gantt » pour voir les tâches sur une chronologie.",
      },
      {
        title: '📈 Faites le bilan avec le Rapport hebdo',
        description: 'Voyez chaque semaine ce que vous avez terminé et ce qui a été reporté.',
      },
      { title: '✅ Cochez cette liste — vous êtes prêt !', description: null },
    ],
  },
};

/** Primary subtag (lowercased) of each supported locale, e.g. 'zh-TW' -> 'zh'. */
const PRIMARY_SUBTAGS: readonly { primary: string; locale: OnboardingLocale }[] =
  ONBOARDING_LOCALES.map((locale) => ({
    primary: (locale.split('-')[0] ?? locale).toLowerCase(),
    locale,
  }));

/** Region / script subtags that mean Traditional Chinese. */
const TRADITIONAL_CHINESE_MARKERS: readonly string[] = ['tw', 'hant', 'hk', 'mo'];

/**
 * Best-effort mapping of an arbitrary BCP-47-ish locale tag (or junk) onto one
 * of the supported onboarding locales. Falls back to 'en'.
 */
export function resolveOnboardingLocale(raw: string | null | undefined): OnboardingLocale {
  if (typeof raw !== 'string') return 'en';
  const tag = raw.trim().toLowerCase().replace(/_/g, '-');
  if (tag.length === 0) return 'en';

  const primary = tag.split('-')[0] ?? '';

  if (primary === 'zh') {
    const traditional = tag
      .split('-')
      .slice(1)
      .some((part) => TRADITIONAL_CHINESE_MARKERS.includes(part));
    return traditional ? 'zh-TW' : 'zh-CN';
  }

  const match = PRIMARY_SUBTAGS.find((entry) => entry.primary === primary);
  return match ? match.locale : 'en';
}
